import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Truck, ArrowUp, ArrowDown, Upload } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [inStock, setInStock] = useState(true);
  const [displayOrder, setDisplayOrder] = useState('0');
  const [shippingUk, setShippingUk] = useState('0');
  const [shippingEu, setShippingEu] = useState('10');
  const [shippingWorld, setShippingWorld] = useState('15');
  const [details, setDetails] = useState('');
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch (e) {
      toast.error('Failed to fetch');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image too large. Maximum 10MB allowed.');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGallerySelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 10MB per image.`);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setGalleryImages(prev => [...prev, reader.result]);
        setGalleryFiles(prev => [...prev, file]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeGalleryImage = (index) => {
    setGalleryImages(prev => prev.filter((_, i) => i !== index));
    setGalleryFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadImage = async () => {
    if (!imageFile) return imageUrl;
    
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      const res = await axios.post(`${API}/upload-product-image`, formData, {
        timeout: 30000 // 30 second timeout
      });
      return res.data.url;
    } catch (e) {
      toast.error('Failed to upload image - try a smaller file');
      return null;
    }
  };

  const uploadGalleryImages = async () => {
    // Keep existing URLs (http, relative /api/uploads/, etc.) — exclude newly selected files
    const existingUrls = galleryImages.filter(img => 
      typeof img === 'string' && !img.startsWith('blob:') && !galleryFiles.some(f => f.preview === img)
    );
    // Filter out only the ones that are already-uploaded URLs (not newly-read data URIs from FileReader)
    const alreadySaved = existingUrls.filter(img => img.startsWith('http') || img.startsWith('/api/'));
    const uploadedUrls = [...alreadySaved];
    
    for (const file of galleryFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/upload-product-image`, formData, {
          timeout: 30000
        });
        uploadedUrls.push(res.data.url);
      } catch (e) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    return uploadedUrls;
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setPrice('');
    setCategory('');
    setImageUrl('');
    setImageFile(null);
    setImagePreview('');
    setGalleryImages([]);
    setGalleryFiles([]);
    setInStock(true);
    setDisplayOrder('0');
    setShippingUk('0');
    setShippingEu('10');
    setShippingWorld('15');
    setDetails('');
    setEditId(null);
    setShowForm(false);
  };

  const openEdit = (p) => {
    setName(p.name);
    setDescription(p.description);
    setPrice(String(p.price));
    setCategory(p.category);
    setImageUrl(p.image_url);
    setImageFile(null);
    setImagePreview(p.image_url);
    setGalleryImages(p.gallery_images || []);
    setGalleryFiles([]);
    setInStock(p.in_stock);
    setDisplayOrder(String(p.display_order || 0));
    setShippingUk(String(p.shipping_uk || 0));
    setShippingEu(String(p.shipping_eu || 10));
    setShippingWorld(String(p.shipping_world || 15));
    setDetails(p.details || '');
    setEditId(p.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Upload image first if there's a new file
    setUploading(true);
    
    let finalImageUrl = imageUrl;
    if (imageFile) {
      finalImageUrl = await uploadImage();
      if (!finalImageUrl) {
        toast.error('Image upload failed');
        setUploading(false);
        return;
      }
    }
    
    // Upload gallery images
    const finalGalleryImages = await uploadGalleryImages();
    
    const data = { 
      name, 
      description, 
      price: parseFloat(price), 
      category, 
      image_url: finalImageUrl,
      gallery_images: finalGalleryImages.length > 0 ? finalGalleryImages : null,
      details: details || null,
      in_stock: inStock,
      display_order: parseInt(displayOrder) || 0,
      shipping_uk: parseFloat(shippingUk) || 0,
      shipping_eu: parseFloat(shippingEu) || 10,
      shipping_world: parseFloat(shippingWorld) || 15
    };
    try {
      if (editId) {
        await axios.put(`${API}/admin/products/${editId}`, data);
        toast.success('Updated');
      } else {
        await axios.post(`${API}/admin/products`, data);
        toast.success('Created');
      }
      fetchProducts();
      resetForm();
    } catch (e) {
      toast.error('Failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`);
      toast.success('Deleted');
      fetchProducts();
    } catch (e) {
      toast.error('Failed');
    }
  };

  const moveProduct = async (product, direction) => {
    const currentOrder = product.display_order || 0;
    const newOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;
    try {
      await axios.put(`${API}/admin/products/${product.id}`, { display_order: newOrder });
      fetchProducts();
    } catch (e) {
      toast.error('Failed to reorder');
    }
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Products</h2>
        <Button onClick={() => setShowForm(true)} className="bg-[#F54021] hover:bg-[#ff5a35]">
          <Plus className="w-4 h-4 mr-2" />Add Product
        </Button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-xl border border-gray-700 overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between p-6 border-b border-gray-700">
              <h3 className="text-xl font-bold text-white">{editId ? 'Edit' : 'Add'} Product</h3>
              <button onClick={resetForm}><X className="w-6 h-6 text-gray-400" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-gray-700 border-gray-600 text-white" required />
                </div>
                <div>
                  <Label className="text-gray-300">Display Order</Label>
                  <Input type="number" value={displayOrder} onChange={(e) => setDisplayOrder(e.target.value)} className="bg-gray-700 border-gray-600 text-white" placeholder="0 = first" />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Price (£)</Label>
                <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="bg-gray-700 border-gray-600 text-white" required />
              </div>
              <div>
                <Label className="text-gray-300">Category</Label>
                <Input value={category} onChange={(e) => setCategory(e.target.value.toLowerCase())} className="bg-gray-700 border-gray-600 text-white" required />
              </div>
              <div>
                <Label className="text-gray-300">Description</Label>
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="bg-gray-700 border-gray-600 text-white" required />
              </div>
              
              <div>
                <Label className="text-gray-300">Product Details</Label>
                <Textarea 
                  value={details} 
                  onChange={(e) => setDetails(e.target.value)} 
                  rows={4} 
                  className="bg-gray-700 border-gray-600 text-white" 
                  placeholder="Enter each detail on a new line, e.g.:&#10;Handcrafted unique piece&#10;Hand finished to a beautiful tactile texture&#10;Free UK delivery"
                />
                <p className="text-gray-500 text-xs mt-1">One detail per line. Shows as bullet points on the product page.</p>
              </div>
              
              {/* Image Upload Section */}
              <div>
                <Label className="text-gray-300">Product Image</Label>
                <div className="mt-2 space-y-3">
                  {/* Upload Button */}
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-600 rounded-lg p-4 text-center cursor-pointer hover:border-[#F54021] transition-colors"
                  >
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-400 text-sm">Click to upload image</p>
                    <p className="text-gray-500 text-xs mt-1">JPG, PNG, WebP</p>
                  </div>
                  <input 
                    ref={fileInputRef}
                    type="file" 
                    accept="image/*" 
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  
                  {/* Preview */}
                  {(imagePreview || imageUrl) && (
                    <div className="relative">
                      <img src={imagePreview || imageUrl} alt="Preview" className="w-full h-40 object-cover rounded-lg" />
                      {imageFile && <span className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">New image selected</span>}
                    </div>
                  )}
                  
                  {/* Or use URL */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-gray-700"></div>
                    <span className="text-gray-500 text-xs">or paste URL</span>
                    <div className="flex-1 h-px bg-gray-700"></div>
                  </div>
                  <Input 
                    value={imageUrl} 
                    onChange={(e) => { setImageUrl(e.target.value); setImageFile(null); setImagePreview(e.target.value); }} 
                    className="bg-gray-700 border-gray-600 text-white text-sm" 
                    placeholder="https://..."
                  />
                </div>
              </div>
              
              {/* Gallery Images */}
              <div>
                <Label className="text-gray-300">Gallery Images (optional)</Label>
                <p className="text-gray-500 text-xs mb-2">Add extra images for the product gallery</p>
                <div className="mt-2 space-y-3">
                  <div 
                    onClick={() => galleryInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-600 rounded-lg p-3 text-center cursor-pointer hover:border-[#d4af37] transition-colors"
                  >
                    <Plus className="w-6 h-6 mx-auto text-gray-400" />
                    <p className="text-gray-400 text-sm">Add gallery images</p>
                  </div>
                  <input 
                    ref={galleryInputRef}
                    type="file" 
                    accept="image/*"
                    multiple
                    onChange={handleGallerySelect}
                    className="hidden"
                  />
                  
                  {galleryImages.length > 0 && (
                    <div className="grid grid-cols-4 gap-2">
                      {galleryImages.map((img, i) => (
                        <div key={`gallery-${img}-${i}`} className="relative group">
                          <img src={img} alt={`Gallery ${i+1}`} className="w-full h-20 object-cover rounded" />
                          <button 
                            type="button"
                            onClick={() => removeGalleryImage(i)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Shipping Section */}
              <div className="pt-4 border-t border-gray-700">
                <div className="flex items-center gap-2 mb-4">
                  <Truck className="w-5 h-5 text-[#d4af37]" />
                  <Label className="text-[#d4af37] font-semibold">Shipping Costs (£)</Label>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-gray-400 text-sm">UK</Label>
                    <Input type="number" step="0.01" value={shippingUk} onChange={(e) => setShippingUk(e.target.value)} className="bg-gray-700 border-gray-600 text-white" placeholder="0" />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">EU</Label>
                    <Input type="number" step="0.01" value={shippingEu} onChange={(e) => setShippingEu(e.target.value)} className="bg-gray-700 border-gray-600 text-white" placeholder="10" />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Rest of World</Label>
                    <Input type="number" step="0.01" value={shippingWorld} onChange={(e) => setShippingWorld(e.target.value)} className="bg-gray-700 border-gray-600 text-white" placeholder="15" />
                  </div>
                </div>
                <p className="text-gray-500 text-xs mt-2">Set to 0 for free shipping</p>
              </div>
              
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} />
                <Label className="text-gray-300">In Stock</Label>
              </div>
              <div className="flex gap-4 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1 border-gray-600">Cancel</Button>
                <Button type="submit" disabled={uploading} className="flex-1 bg-[#F54021]">
                  {uploading ? 'Uploading...' : (editId ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((p, index) => (
          <div key={p.id} className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden relative">
            {/* Order badge */}
            <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
              #{p.display_order || 0}
            </div>
            {/* Move buttons */}
            <div className="absolute top-2 right-2 flex gap-1">
              <button 
                onClick={() => moveProduct(p, 'up')} 
                className="bg-black/70 text-white p-1 rounded hover:bg-[#F54021] transition-colors"
                title="Move up"
              >
                <ArrowUp className="w-4 h-4" />
              </button>
              <button 
                onClick={() => moveProduct(p, 'down')} 
                className="bg-black/70 text-white p-1 rounded hover:bg-[#F54021] transition-colors"
                title="Move down"
              >
                <ArrowDown className="w-4 h-4" />
              </button>
            </div>
            <img src={p.image_url} alt={p.name} className="w-full h-48 object-cover" />
            <div className="p-4">
              <span className="text-xs text-[#F54021] uppercase">{p.category}</span>
              <h3 className="text-white font-bold mt-1">{p.name}</h3>
              <p className="text-gray-400 text-sm mt-1 line-clamp-2">{p.description}</p>
              <p className="text-[#d4af37] font-bold mt-2">£{Math.floor(p.price)}</p>
              <p className="text-gray-500 text-xs mt-1">
                Shipping: UK £{p.shipping_uk || 0} / EU £{p.shipping_eu || 10} / World £{p.shipping_world || 15}
              </p>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="flex-1 border-gray-600">
                  <Pencil className="w-4 h-4 mr-1" />Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDelete(p.id)} className="border-red-600 text-red-400">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {products.length === 0 && <div className="text-center py-12 text-gray-400">No products yet.</div>}
    </div>
  );
}
